// "New chat": pick a person and open the one-to-one chat with them.
//
// The app's "New message" screen (apps/mobile/src/screens/social/DmDirectoryScreen.tsx),
// with the same routes: the person's own team before anything is typed, a search of the
// project from two letters, and POST /social-dm/conversations with the project and the
// person, which opens the chat they already have or makes it. The server decides who
// may be listed and who may be messaged; this pop-up shows what it answers.
//
// Mounted only while it is open, so every opening starts with an empty box.
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Spinner, InlineLoader } from '../loading';
import { messagesAPI, type PickPerson } from '../../services/messagesApi';
import MessagesDialog from './MessagesDialog';
import { Face, FindFailureLine, FindField, ListHeading, Quiet } from './PickParts';
import { pickLine, usePeopleFinder } from './usePeopleFinder';

interface NewChatDialogProps {
  projectId: number;
  onClose: () => void;
  /** The chat is open on the server: the page shows it. */
  onOpened: (conversationId: string, person: PickPerson) => void;
}

const ROW = 'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-default disabled:hover:bg-transparent';

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;

/** Why a chat did not open, in one line. */
const openRefusal = (err: unknown): string => {
  const status = statusOf(err);
  // The server's one refusal: this person cannot be messaged by this one, here.
  if (status === 404 || status === 403) return 'This person cannot be messaged.';
  if (status === 401) return 'Your sign-in could not be confirmed.';
  return 'The chat could not be opened. Try again.';
};

export default function NewChatDialog({ projectId, onClose, onOpened }: NewChatDialogProps): ReactElement {
  const [typed, setTyped] = useState('');
  const find = useRef<HTMLInputElement>(null);
  const { people, loading, failure, searching, retry } = usePeopleFinder(projectId, typed, messagesAPI.people);
  const [opening, setOpening] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  // Stops a second click that lands before the first has redrawn the rows.
  const openingNow = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const open = async (person: PickPerson) => {
    if (openingNow.current) return;
    openingNow.current = true;
    setOpening(person.empCode);
    setRefusal(null);
    try {
      const res = await messagesAPI.openChat(projectId, person.empCode);
      const id = res?.data?.data?.conversationId;
      if (!alive.current) return;
      if (id) onOpened(String(id), person);
      else setRefusal('The chat could not be opened. Try again.');
    } catch (err) {
      if (alive.current) setRefusal(openRefusal(err));
    } finally {
      openingNow.current = false;
      if (alive.current) setOpening(null);
    }
  };

  let body: ReactElement;
  if (failure) {
    body = <FindFailureLine failure={failure} off="Direct messages are not available here." onRetry={retry} />;
  } else if (people.length === 0) {
    body = loading
      ? <InlineLoader label="Loading people…" className="flex justify-center py-6" />
      : <Quiet>{searching ? 'No one by that name.' : 'Type a name to find someone.'}</Quiet>;
  } else {
    body = (
      <>
        <ListHeading searching={searching} />
        <ul aria-label="People" className="space-y-0.5">
          {people.map((person) => (
            <li key={person.empCode}>
              <button
                type="button"
                onClick={() => { void open(person); }}
                disabled={opening !== null}
                aria-label={`Message ${person.name}`}
                className={ROW}
              >
                <Face name={person.name} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">{person.name}</span>
                  <span className="block truncate text-xs text-gray-500">{pickLine(person)}</span>
                </span>
                {opening === person.empCode && <Spinner size="sm" />}
              </button>
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <MessagesDialog title="New chat" onClose={onClose} initialFocus={find}>
      <div className="flex-none px-5 pb-3">
        <FindField value={typed} onChange={setTyped} busy={loading && people.length > 0} inputRef={find} />
        {refusal && <p role="alert" className="mt-2 text-sm text-red-600">{refusal}</p>}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">{body}</div>
    </MessagesDialog>
  );
}
